import BlurInText from "@/components/ui/blur-in-text";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import type {
  AskClarificationToolOutput,
  ClarificationSkip,
} from "@shaarp/common";
import {
  ArrowRightIcon,
  ChevronsRightIcon,
  PencilLineIcon,
  SearchIcon,
} from "lucide-react";
import {
  memo,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ToolUIPart } from "ai";
import TextareaAutosize from "react-textarea-autosize";
import { getClarificationButtonMessage } from "./chat-clarification-messages";
import type {
  ClarificationReply,
  ClarificationStep,
} from "./chat-clarification-run";

type ReadStep = {
  readonly id: string;
  readonly question: string;
  readonly reason?: string;
  readonly options: AskClarificationToolOutput["options"];
  readonly answer?: string;
  /** The reply walked past the question, so it settled no criterion. */
  readonly isSkipped: boolean;
  /** The call is still being written, so its text is arriving word by word. */
  readonly isWriting: boolean;
};

/**
 * Length past which a free-text answer is folded to three lines, and given a
 * way to unfold.
 *
 * Whether three lines actually clamp depends on the width — measuring it would
 * re-run on every stream delta of every card in the thread, so the character
 * count stands in for it. The fold and the button are the same condition on
 * purpose: clamping on its own, on an answer the count says is short, hides
 * the end of a reply in the one place it can still be read back.
 */
const ANSWER_FOLDED_LENGTH = 160;

/**
 * A reply the user typed, folded to a few lines with a way to unfold it.
 *
 * The bubble it was written in is not rendered — the run swallowed it — so
 * this is the only place a long answer can be read back in full.
 */
function AnswerText({ text }: { text: string }): ReactNode {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const isFoldable = text.length > ANSWER_FOLDED_LENGTH;

  return (
    <>
      <p
        className={cn(
          "mt-1 text-base font-semibold text-foreground",
          isFoldable && !isExpanded && "line-clamp-3"
        )}
      >
        {text}
      </p>
      {isFoldable && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-0.5 text-xs underline text-muted-foreground hover:text-foreground"
        >
          {t(isExpanded ? "common.seeLess" : "common.seeMore")}
        </button>
      )}
    </>
  );
}

/**
 * The controls the open question offers: its suggested answers, a free-text
 * field, and the two ways out of the questioning.
 *
 * A component of its own, mounted with the step's id as `key`, because that is
 * what makes the editor's state belong to the question rather than to the
 * card: a draft abandoned for an option cannot reappear under the next one,
 * with no effect to keep the two in step.
 */
function OpenQuestion({
  step,
  disabled,
  shouldTakeFocus,
  onReply,
}: {
  step: ReadStep;
  /** A turn is running, so no answer can be sent right now. */
  disabled: boolean;
  /** A previous question was just answered, so the focus has nowhere to land. */
  shouldTakeFocus: boolean;
  onReply: (text: string, skipped?: ClarificationSkip) => void;
}): ReactNode {
  const { t, currentLanguage } = useTranslation();
  const [isWritingCustom, setIsWritingCustom] = useState(false);
  const [draft, setDraft] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // The button that was clicked is unmounted when its row settles, and the
    // focus falls back to the body: a keyboard user would have to walk the
    // whole thread again to reach the question that just opened.
    if (shouldTakeFocus) containerRef.current?.focus();
  }, [shouldTakeFocus]);

  /**
   * Whether the controls are inert.
   *
   * Two reasons, and the second is the one that is easy to miss: a question
   * whose call was cut mid-write keeps the input it had got that far, and the
   * thread goes back to `ready` around it — so an option reading "Sur Montpel"
   * is left clickable, and clicking it sends that as the user's answer.
   */
  const isInert = disabled || step.isWriting;

  /**
   * The one way a reply leaves this question, whichever control sent it.
   *
   * It carries the guard because the controls cannot: a turn can start
   * streaming while the free-text field is open, and an `Enter` there would
   * send a message on top of a turn already in flight.
   */
  const reply = (text: string, skipped?: ClarificationSkip): void => {
    if (isInert) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    setIsWritingCustom(false);
    setDraft("");
    onReply(trimmed, skipped);
  };

  return (
    // On a phone (`max-md:`) the card reads at the size of the conversation
    // around it and every control is a 44px target, with room between them:
    // the desktop's compact rows read as one dense block at 375px.
    <div ref={containerRef} tabIndex={-1} className="outline-none">
      <p className="text-sm font-semibold text-foreground max-md:text-base">
        <BlurInText text={step.question} isWriting={step.isWriting} />
      </p>
      {step.reason && (
        <p className="mt-1 text-xs text-muted-foreground max-md:mt-1.5 max-md:text-sm">
          <BlurInText text={step.reason} isWriting={step.isWriting} />
        </p>
      )}

      {step.options.length > 0 && (
        <div className="mt-3 space-y-2 max-md:mt-4 max-md:space-y-3">
          {step.options.map((option, optionIndex) => (
            <button
              // Keyed on the position: a label still streaming is half-built
              // and would remount the row on every delta.
              key={optionIndex}
              type="button"
              disabled={isInert}
              onClick={() => reply(option.label)}
              className={cn(
                "group flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left transition-colors",
                "max-md:min-h-11 max-md:rounded-xl max-md:px-4 max-md:py-3 active:bg-majorelle/5",
                "hover:border-majorelle hover:bg-majorelle/5",
                "disabled:pointer-events-none disabled:opacity-50"
              )}
            >
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-foreground max-md:text-base">
                  <BlurInText text={option.label} isWriting={step.isWriting} />
                </span>
                {option.description && (
                  // Two lines at most on a phone: the label is the choice,
                  // the description only tells two close ones apart.
                  <span className="block mt-0.5 text-xs text-muted-foreground max-md:mt-1 max-md:line-clamp-2 max-md:text-sm">
                    <BlurInText
                      text={option.description}
                      isWriting={step.isWriting}
                    />
                  </span>
                )}
              </span>
              <ArrowRightIcon className="mt-1 opacity-0 transition-opacity size-3.5 shrink-0 text-majorelle group-hover:opacity-100 touch:opacity-100" />
            </button>
          ))}
        </div>
      )}

      {isWritingCustom ? (
        <div className="p-2 mt-2 rounded-lg border transition-colors focus-within:border-majorelle">
          <TextareaAutosize
            autoFocus
            disabled={isInert}
            value={draft}
            minRows={1}
            maxRows={6}
            aria-label={t("chat.message.question.customAnswer")}
            placeholder={t("chat.message.question.customPlaceholder")}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.preventDefault();
                setIsWritingCustom(false);
                setDraft("");
                return;
              }
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                reply(draft);
              }
            }}
            className="px-1 w-full text-base md:text-sm bg-transparent border-0 shadow-none resize-none focus:outline-none"
          />
          <div className="flex gap-2 justify-end mt-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsWritingCustom(false);
                setDraft("");
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={isInert || !draft.trim()}
              onClick={() => reply(draft)}
            >
              {t("chat.input.send")}
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={isInert}
          onClick={() => setIsWritingCustom(true)}
          className={cn(
            "flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm font-medium text-foreground transition-colors",
            "max-md:min-h-11 max-md:rounded-xl max-md:px-4 max-md:text-base active:bg-majorelle/5",
            step.options.length > 0 ? "mt-2 max-md:mt-3" : "mt-3",
            "hover:border-majorelle hover:bg-majorelle/5",
            "disabled:pointer-events-none disabled:opacity-50"
          )}
        >
          <PencilLineIcon className="size-4 shrink-0 text-muted-foreground" />
          {t("chat.message.question.customAnswer")}
        </button>
      )}

      {/* Both ways out, spelled out, side by side and outside the editor: the
          two are a choice between them, and skipping used to mean cancelling
          the draft first. The list's bottom padding is pulled in by the same
          amount, so the row sits one option gap from what is above it and from
          the card's edge below it. */}
      <div className="flex flex-wrap gap-2 justify-end mt-2 -mb-2">
        <button
          type="button"
          disabled={isInert}
          onClick={() =>
            reply(
              getClarificationButtonMessage("skipQuestion", currentLanguage),
              "question",
            )
          }
          className="flex gap-2 items-center px-3 py-2 text-sm rounded-lg transition-colors text-muted-foreground hover:bg-muted hover:text-foreground active:bg-muted disabled:pointer-events-none disabled:opacity-50 max-md:min-h-11"
        >
          {t("chat.message.question.skip")}
          <ChevronsRightIcon className="size-4 shrink-0" />
        </button>
        <button
          type="button"
          disabled={isInert}
          onClick={() =>
            reply(
              getClarificationButtonMessage("searchNow", currentLanguage),
              "all",
            )
          }
          className="flex gap-2 items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors text-majorelle hover:bg-majorelle/5 active:bg-majorelle/5 disabled:pointer-events-none disabled:opacity-50 max-md:min-h-11"
        >
          <SearchIcon className="size-4 shrink-0" />
          {t("chat.message.question.searchNow")}
        </button>
      </div>
    </div>
  );
}

/**
 * What a step actually shows: the streaming input is read before the settled
 * output, so a question appears as it is written rather than once the call
 * completes.
 */
function readStep(step: ClarificationStep): ReadStep | null {
  const { part } = step;
  const streamed = part.input as Partial<AskClarificationToolOutput> | undefined;
  const settled =
    part.state === "output-available"
      ? (part.output as AskClarificationToolOutput)
      : undefined;

  const question = settled?.question ?? streamed?.question;
  if (!question) return null;

  return {
    id: step.id,
    question,
    reason: settled?.reason ?? streamed?.reason,
    // Options stream in one field at a time, so the last one is half-built.
    options: (settled?.options ?? streamed?.options ?? []).filter(
      (option) => !!option?.label
    ),
    answer: step.answer,
    isSkipped: !!step.isSkipped,
    isWriting: part.state === "input-streaming",
  };
}

type ChatClarificationCardProps = {
  /** The run's questions in order; only the last one may still be open. */
  steps: readonly ClarificationStep[];
  /** Whether the run ends the thread, so its last question can be answered. */
  isTail: boolean;
  /** A turn is running, so no answer can be sent right now. */
  disabled?: boolean;
  onAnswer?: (answer: string, skipped?: ClarificationSkip) => void;
};

/**
 * The card a run of `askClarification` calls renders as: one block holding
 * every question the assistant asked to aim a search, the reply each one got,
 * and — at the bottom — the one still open with its suggested answers as
 * buttons and a free-text field for everything else.
 *
 * The assistant asks one question per turn, so a run arrives spread over
 * several messages; merging them here is what keeps a coaching exchange reading
 * as one conversation instead of a stack of cards. A reply travels as an
 * ordinary user message, which is why the card only hands the text back to its
 * caller.
 */
function ChatClarificationCardBase({
  steps,
  isTail,
  disabled = false,
  onAnswer,
}: ChatClarificationCardProps): ReactNode {
  const { t } = useTranslation();
  const headingId = useId();

  const asked = steps
    .map(readStep)
    .filter((step): step is ReadStep => step !== null);
  const lastIndex = asked.length - 1;
  const openStepId =
    isTail && lastIndex >= 0 && !asked[lastIndex].answer
      ? asked[lastIndex].id
      : undefined;

  // Which question was open when the card last rendered, and whether it has
  // rendered at all. A card arriving with one already open — a reload, a
  // scroll back up — must not pull the focus; one that opens *while* the card
  // is on screen must, because the button that was clicked has just been
  // unmounted and the focus fell back to the body.
  //
  // The second half is what the comparison alone could not do. A reply is
  // attached a render before the next question arrives, so the open step goes
  // `q1 → undefined → q2`, and gating on "there was a question open before"
  // read the empty render as the arrival: it fired where nothing was mounted
  // to take the focus, and stayed silent on the render that had one.
  const shownOpenStepIdRef = useRef<string | undefined>(undefined);
  const hasRenderedRef = useRef(false);
  const shouldTakeFocus =
    hasRenderedRef.current && shownOpenStepIdRef.current !== openStepId;
  useEffect(() => {
    hasRenderedRef.current = true;
    shownOpenStepIdRef.current = openStepId;
  }, [openStepId]);

  // The tool part reaches the thread before the first character of its
  // question does, and the message it arrives in has already replaced the
  // thinking indicator. Rendering nothing for that window is what made the
  // whole line vanish and come back on a run of a single question.
  //
  // Only while it is still being written: a call that settled without a
  // question — refused, or cut mid-flight — has nothing to wait for, and the
  // dots would spin over it for good.
  if (asked.length === 0) {
    if (!steps.some((step) => step.part.state === "input-streaming")) {
      return null;
    }

    return (
      <section
        aria-labelledby={headingId}
        className="overflow-hidden w-full rounded-xl border bg-background not-prose"
      >
        <div className="px-4 py-3 border-b">
          <h3
            id={headingId}
            className="text-xs font-semibold tracking-wider uppercase text-majorelle"
          >
            {t("chat.message.question.title")}
          </h3>
        </div>
        {/* Announced, not just drawn: the question arrives on its own, with no
            click behind it, so a screen reader otherwise gets no sign that the
            answer it was waiting for is a card asking something back. */}
        <div role="status" aria-label={t("chat.message.question.waiting")}>
          <div aria-hidden className="flex gap-1 px-4 py-4">
            <span className="rounded-full bg-majorelle size-1.5 thinking-dot" />
            <span className="rounded-full bg-majorelle size-1.5 thinking-dot [animation-delay:0.2s]" />
            <span className="rounded-full bg-majorelle size-1.5 thinking-dot [animation-delay:0.4s]" />
          </div>
        </div>
      </section>
    );
  }

  // A question walked past settled nothing, so it counts towards no precision —
  // a run the user skipped through reads "0 / 3", which is what it is.
  const answeredCount = asked.filter(
    (step) => !!step.answer && !step.isSkipped
  ).length;
  // "Precisions kept" over "0 / 3" is a contradiction: a run the user skipped
  // through kept none, so it keeps the open title even though every question
  // has been dealt with.
  const hasEveryReply =
    answeredCount > 0 && asked.every((step) => !!step.answer);

  return (
    <section
      aria-labelledby={headingId}
      className="overflow-hidden w-full rounded-xl border bg-background not-prose"
    >
      <div className="flex gap-3 justify-between items-center px-4 py-3 border-b">
        <h3
          id={headingId}
          className="text-xs font-semibold tracking-wider uppercase text-majorelle"
        >
          {t(
            hasEveryReply
              ? "chat.message.question.recapTitle"
              : "chat.message.question.title"
          )}
        </h3>
        {/* "1 / 3" is read out as "one slash three": the ratio is spelled out
            for a screen reader and hidden from it on screen. */}
        <p aria-hidden className="text-xs tabular-nums text-muted-foreground">
          {answeredCount} / {asked.length}
        </p>
        <span className="sr-only">
          {t("chat.message.question.progress", {
            answered: answeredCount,
            count: asked.length,
          })}
        </span>
      </div>

      <ol className="p-4">
        {asked.map((step, index) => {
          const isOpen = step.id === openStepId;

          const row = (content: ReactNode) => (
            <li key={step.id} className="relative flex gap-3 pb-5 last:pb-0">
              {index !== lastIndex && (
                <span
                  aria-hidden
                  // Dot edge to dot edge: the next row's dot sits 6px below
                  // this row's box, past where the bottom padding stops.
                  className="absolute -bottom-1.5 left-[3.5px] top-3.5 w-px bg-majorelle/25"
                />
              )}
              <span
                aria-hidden
                className={cn(
                  "mt-1.5 size-2 shrink-0 rounded-full",
                  step.answer && "bg-majorelle",
                  step.isSkipped && "bg-muted-foreground/40",
                  isOpen && "bg-majorelle ring-4 ring-majorelle/15",
                  !step.answer && !isOpen && "border border-majorelle/40"
                )}
              />
              <div className="flex-1 min-w-0">{content}</div>
            </li>
          );

          if (step.answer) {
            return row(
              <>
                <p className="text-sm text-muted-foreground">{step.question}</p>
                {/* A skip is a sentence the user did not write: showing it where
                    an answer goes would read as a precision they gave. */}
                {step.isSkipped ? (
                  <span className="inline-block px-3 py-1.5 mt-2 text-sm rounded-lg bg-muted text-muted-foreground">
                    {t("chat.message.question.skipped")}
                  </span>
                ) : (
                  <AnswerText text={step.answer} />
                )}
              </>
            );
          }

          // A question left behind — the user typed something else, or the
          // conversation was picked back up further down — never offers its
          // buttons again: clicking one would answer below an answer.
          if (!isOpen) {
            return row(
              <>
                <p className="text-sm text-muted-foreground">{step.question}</p>
                <span className="inline-block px-3 py-1.5 mt-2 text-sm rounded-lg bg-muted text-muted-foreground">
                  {t("chat.message.question.unanswered")}
                </span>
              </>
            );
          }

          return row(
            <OpenQuestion
              key={step.id}
              step={step}
              disabled={disabled}
              shouldTakeFocus={shouldTakeFocus}
              onReply={(text, skipped) => onAnswer?.(text, skipped)}
            />
          );
        })}
      </ol>
    </section>
  );
}

/**
 * The card for a question that could not join a run — one asked under a lead-in
 * sentence, or beside another tool call — built from the single part it lives
 * in.
 *
 * Its own component so that the step is assembled in one place: the message and
 * the thread both render this card, and a hand-built `steps` array on each side
 * is a field that reaches only one of them the day one is added.
 */
export function ChatClarificationPartCard({
  id,
  part,
  reply,
  isTail,
  disabled,
  onAnswer,
}: {
  /** Identifies the part within its message, and keys the step. */
  id: string;
  part: ToolUIPart;
  /** The reply the thread resolved for it, when it got one. */
  reply?: ClarificationReply;
  isTail: boolean;
  disabled?: boolean;
  onAnswer?: (answer: string, skipped?: ClarificationSkip) => void;
}): ReactNode {
  return (
    <ChatClarificationCard
      steps={[
        {
          id,
          part,
          ...(reply && { answer: reply.text, isSkipped: reply.isSkipped }),
        },
      ]}
      isTail={isTail}
      disabled={disabled}
      onAnswer={onAnswer}
    />
  );
}

/**
 * Memoised on the steps themselves, field by field.
 *
 * Every stream delta re-renders the whole thread, and a settled run is a card
 * that will never change again — but the thread rebuilds its steps array on
 * each pass, so a bare `memo` would compare two fresh arrays and never hit.
 */
export const ChatClarificationCard = memo(
  ChatClarificationCardBase,
  (previous, next) =>
    previous.isTail === next.isTail &&
    previous.disabled === next.disabled &&
    previous.onAnswer === next.onAnswer &&
    previous.steps.length === next.steps.length &&
    previous.steps.every((step, index) => {
      const other = next.steps[index];
      return (
        step.id === other.id &&
        step.answer === other.answer &&
        step.isSkipped === other.isSkipped &&
        step.part.state === other.part.state &&
        step.part.input === other.part.input &&
        step.part.output === other.part.output
      );
    })
);
